import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { useHistory, withRouter } from 'react-router-dom';
import compose from 'lodash.flowright';
import { connect } from 'react-redux';
import { graphql } from 'react-apollo';

import { AlbumFolderForVideoGuestBook } from '../../../../../../graphql/queries/album-folder';
import { MeQuery } from '../../../../../../graphql/queries/account';
import { isAlbumFolderOwner } from '../../../../../../util/account';
import { VIDEO_TYPE } from '../../../../../../constants/videos';
import VideoGuestBookContributorBanner from '../VideoGuestBookContributorBanner';
import useBasename from '../../../../../../components/routing/useBasename';
import VideoGuestBookGreetingModal, {
  VIDEO_GUEST_BOOK_GREETING_MODAL,
} from '../VideoGuestBookGreetingModal';
import useDismissiblePersistent from '../../../../../../hooks/useDismissiblePersistent';
import { STORE_NAMESPACE } from '../../../../../../components/global/DismissiblePersistent';
import useOpenModal from '../../../../../../hooks/useOpenModal';
import ContributeButton from '../../../../../../components/global/ContributeButton';
import { AlbumForVideoGuestBook } from '../../../../../../graphql/queries/albums';

const propTypes = {
  user: PropTypes.shape({
    id: PropTypes.string,
  }),
  albumFolder: PropTypes.shape({
    id: PropTypes.string,
    children: PropTypes.shape({
      albums: PropTypes.shape({
        records: PropTypes.arrayOf(
          PropTypes.shape({
            id: PropTypes.string,
            is_video_album: PropTypes.bool,
          }),
        ),
      }),
    }),
  }),
  album: PropTypes.shape({
    id: PropTypes.string,
  }).isRequired,
};

const VideoGuestBookLanding = ({ albumFolder, album, user }) => {
  const history = useHistory();
  const basename = useBasename();
  const videoAlbum =
    album ||
    (albumFolder?.children?.albums?.records ?? []).find(
      albumRecord => albumRecord.is_video_album,
    );
  const isAlbumFolderOwnerBool = isAlbumFolderOwner(user, albumFolder);
  const greetingVideo = (videoAlbum?.videos?.records ?? []).find(
    video => video.type === VIDEO_TYPE.GREETING,
  );
  const openModal = useOpenModal();
  const { visible, onDismiss } = useDismissiblePersistent(
    `${STORE_NAMESPACE}_VGB_${videoAlbum?.id}`,
  );

  useEffect(() => {
    if (
      user &&
      visible &&
      videoAlbum &&
      isAlbumFolderOwnerBool &&
      !greetingVideo
    ) {
      onDismiss();
      openModal(VIDEO_GUEST_BOOK_GREETING_MODAL);
    }
  }, [albumFolder?.id, visible, user?.id]);

  if (!videoAlbum || !albumFolder) {
    // Nothing to do, irrelevant to be here.
    return null;
  }

  if (isAlbumFolderOwnerBool) {
    // Solicit recording:
    return (
      <VideoGuestBookGreetingModal
        onClose={onDismiss}
        onRecordGreeting={() => {
          history.push(`/app/${basename}/albums/${videoAlbum.id}/record-video`);
        }}
      />
    );
  }

  if (!isAlbumFolderOwnerBool) {
    // It may solicit recording or show video greeting. Depends on whether a greeting has been recorded or not.
    // We use contribute button so that we can guarantee that even un-authed users can interact here.
    return (
      <>
        <ContributeButton albumId={videoAlbum.id}>
          {({ onClick }) => (
            <>
              <VideoGuestBookContributorBanner
                greetingVideo={greetingVideo}
                user={user}
                albumFolder={albumFolder}
                album={videoAlbum}
                onRecordGreeting={() => {
                  onClick();
                }}
              />
            </>
          )}
        </ContributeButton>
      </>
    );
  }

  return null;
};

VideoGuestBookLanding.propTypes = propTypes;

const withAlbumFolderForVideoGuestBook = graphql(AlbumFolderForVideoGuestBook, {
  options: ({ match, user }) => ({
    variables: {
      id:
        match.params.folderId ??
        match.params.albumFolderId ??
        match.params.hierarchyItemId,
      user_uploaded_videos: user?.id,
      include_user_uploaded_videos: !!user?.id,
    },
  }),
  props: ({ data: { albumFolder, loading } }) => ({
    albumFolder,
    loading,
  }),
  skip: ({ match }) =>
    !(
      match.params.folderId ??
      match.params.albumFolderId ??
      match.params.hierarchyItemId
    ),
});
const withAlbumForVideoGuestBook = graphql(AlbumForVideoGuestBook, {
  options: ({ match, user }) => ({
    variables: {
      id: match.params.albumId,
      user_uploaded_videos: user?.id,
      include_user_uploaded_videos: !!user?.id,
    },
  }),
  props: ({ data: { album, loading } }) => ({
    album,
    albumFolder: album?.album_folder,
    loading,
  }),
  skip: ({ match }) => !match.params.albumId,
});

const withMeQuery = graphql(MeQuery, {
  props: ({ data: { me, loading }, ownProps }) => ({
    loading: ownProps.loading || loading,
    user: me,
  }),
  skip: ({ authenticated }) => !authenticated,
});

const mapStateToProps = ({ auth }) => ({
  authenticated: auth.authenticated,
});

export default compose(
  withRouter,
  connect(mapStateToProps),
  withMeQuery,
  withAlbumFolderForVideoGuestBook,
  withAlbumForVideoGuestBook,
)(VideoGuestBookLanding);
